import { useOnboarding } from "@onboardjs/react";
import { useMutation, useQuery } from "convex/react";
import { Loader2, User } from "lucide-react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../components/ui/button";
import { MarkdownEditor } from "../../components/ui/markdown-editor";
import { Skeleton } from "../../components/ui/skeleton";

const STATUS_MESSAGES: Record<string, { title: string; description: string }> =
	{
		pending: {
			title: "Preparing profile extraction...",
			description: "Getting ready to analyze your communication style.",
		},
		fetching_emails: {
			title: "Pulling your recent sent emails...",
			description:
				"Reading your sent folder to understand your communication patterns and signature.",
		},
		extracting: {
			title: "Analyzing your communication style...",
			description:
				"Using AI to extract your signature, preferences, and communication patterns from your emails.",
		},
	};

const DEFAULT_PROFILE = `## Role
Title:
Specialties:
Authority:
Reports to:

## Signature
[Your Name]
[Your Title]
[Firm Name]
[Phone]
[Email]

## Email Preferences
- Closing: "Best regards,"

## Scheduling
Available: Mon-Fri, 9-5
Default meeting length: 30 min
Buffer: 15 min

## Notes
`;

export function PersonProfileStep() {
	const { next } = useOnboarding();
	const personExtraction = useQuery(api.codex.queries.getPersonExtraction);
	const savePersonProfile = useMutation(api.codex.mutations.savePersonProfile);

	const [saving, setSaving] = useState(false);

	const isExtracting =
		personExtraction?.status === "pending" ||
		personExtraction?.status === "fetching_emails" ||
		personExtraction?.status === "extracting";
	const isComplete = personExtraction?.status === "complete";
	const isFailed = personExtraction?.status === "failed";

	const initialContent = personExtraction?.extractedProfile ?? DEFAULT_PROFILE;
	const [profileContent, setProfileContent] = useState(initialContent);

	// Update content when extraction completes
	if (
		isComplete &&
		personExtraction?.extractedProfile &&
		profileContent === DEFAULT_PROFILE
	) {
		setProfileContent(personExtraction.extractedProfile);
	}

	const handleSave = async () => {
		setSaving(true);
		try {
			// Extract name from the profile content (first line of ## Signature section)
			const signatureMatch = profileContent.match(/## Signature\n([^\n]+)/);
			const name = signatureMatch?.[1]?.trim() ?? "Person Profile";

			await savePersonProfile({
				name,
				content: profileContent,
			});
			await next();
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			{/* Extraction Status */}
			{isExtracting && personExtraction?.status && (
				<div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
					<Loader2 className="h-5 w-5 animate-spin text-primary" />
					<div>
						<p className="font-medium text-foreground">
							{STATUS_MESSAGES[personExtraction.status]?.title ??
								"Processing..."}
						</p>
						<p className="text-muted-foreground text-sm">
							{STATUS_MESSAGES[personExtraction.status]?.description ??
								"Please wait while we analyze your profile."}
						</p>
					</div>
				</div>
			)}

			{isFailed && (
				<div className="rounded-lg border border-warning/30 bg-warning/10 p-4">
					<p className="text-foreground text-sm">
						We couldn't automatically extract your profile from your emails.
						Please fill in the details manually below.
					</p>
				</div>
			)}

			{/* Profile Editor */}
			{isExtracting ? (
				<div className="flex flex-col gap-3">
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-2/3" />
					<Skeleton className="h-32 w-full" />
					<Skeleton className="h-4 w-1/2" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-full" />
				</div>
			) : (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<User className="h-4 w-4 text-muted-foreground" />
						<p className="text-sm text-muted-foreground">
							{isComplete
								? "We extracted your profile from your sent emails. Review and edit below."
								: "Fill in your profile details. This tells Clerical how to draft emails and manage communications on your behalf."}
						</p>
					</div>
					<MarkdownEditor
						value={profileContent}
						onChange={(md) => setProfileContent(md)}
						placeholder="Enter your profile..."
					/>
				</div>
			)}

			<div className="flex justify-end pt-2">
				<Button onClick={handleSave} disabled={saving || isExtracting}>
					{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					Complete Setup
				</Button>
			</div>
		</div>
	);
}
